<template>
  <div class="container-fluid pt-4">
    <div class="row justify-content-center">
      <div class="col-lg-8 col-xl-6">
        <!-- Main Card -->
        <div class="main-card">
          <h2 class="mb-1">Activación Masiva de Estudiantes</h2>
          <p class="text-muted mb-0">Activa o desactiva múltiples estudiantes mediante un archivo Excel</p>
          <form @submit.prevent="procesarArchivo" autocomplete="off">

            <!-- Action Selector -->
            <div class="section-card mb-4">
              <h5 class="section-title">
                <i class="fa fa-tasks mr-2"></i>Selecciona la acción
              </h5>
              <div class="action-selector">
                <div class="action-option" :class="{ 'active': accion === 'desactivar' }"
                  @click="accion = 'desactivar'">
                  <div class="radio-custom">
                    <div class="radio-inner" v-if="accion === 'desactivar'"></div>
                  </div>
                  <div class="action-content">
                    <i class="fa fa-user-slash action-icon text-danger"></i>
                    <div>
                      <strong>Desactivar</strong>
                      <p class="mb-0 text-muted small">Desactivar estudiantes seleccionados</p>
                    </div>
                  </div>
                </div>

                <div class="action-option" :class="{ 'active': accion === 'activar' }" @click="accion = 'activar'">
                  <div class="radio-custom">
                    <div class="radio-inner" v-if="accion === 'activar'"></div>
                  </div>
                  <div class="action-content">
                    <i class="fa fa-user-check action-icon text-success"></i>
                    <div>
                      <strong>Activar</strong>
                      <p class="mb-0 text-muted small">Activar estudiantes seleccionados</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- File Upload -->
            <div class="section-card mb-4">
              <h5 class="section-title">
                <i class="fa fa-file-excel mr-2"></i>Archivo Excel
              </h5>

              <div class="file-upload-wrapper">
                <input type="file" id="fileInput" ref="fileInput" @change="handleFileUpload" accept=".xlsx,.xls"
                  class="file-input">
                <label for="fileInput" class="file-label">
                  <div class="file-icon">
                    <i class="fa fa-cloud-upload-alt fa-3x"></i>
                  </div>
                  <div class="file-text">
                    <span v-if="!archivo" class="file-placeholder">
                      Haz clic aquí o arrastra tu archivo Excel
                    </span>
                    <span v-else class="file-name">
                      <i class="fa fa-file-excel mr-2 text-success"></i>
                      {{ nombreArchivo }}
                    </span>
                  </div>
                  <div class="file-hint">
                    Formatos aceptados: .xlsx, .xls
                  </div>
                </label>

                <button v-if="archivo" type="button" class="btn-clear-file" @click.prevent="limpiarArchivo">
                  <i class="fa fa-times"></i>
                </button>
              </div>

              <!-- Download Demo -->
              <div class="demo-download mt-3">
                <a href="/demos/ejemplo_activacion_masiva.xlsx" download class="demo-link">
                  <i class="fa fa-download mr-2"></i>
                  Descargar archivo de ejemplo
                </a>
              </div>

              <!-- File Structure Info -->
              <div class="info-box mt-3">
                <div class="info-header">
                  <i class="fa fa-info-circle mr-2"></i>
                  Estructura del archivo
                </div>
                <div class="info-content">
                  <p class="mb-2"><strong>Formato requerido:</strong></p>
                  <ul class="mb-0">
                    <li>Primera columna: <code>Número de Documento</code></li>
                    <li>Una fila por estudiante</li>
                    <li>Sin encabezados (solo los números de documento)</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Errors -->
            <div v-if="errores.length > 0" class="section-card error-card mb-4">
              <h5 class="section-title text-danger">
                <i class="fa fa-exclamation-triangle mr-2"></i>Errores encontrados
              </h5>
              <div class="error-list">
                <div v-for="(error, index) in errores" :key="index" class="error-item">
                  <i class="fa fa-times-circle mr-2"></i>
                  {{ error }}
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
              <button type="button" class="btn btn-outline-secondary btn-lg" @click="resetear">
                <i class="fa fa-redo mr-2"></i>Limpiar
              </button>
              <button type="submit" class="btn btn-primary btn-lg" :disabled="!archivo || procesando">
                <i class="fa" :class="procesando ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                {{ procesando ? 'Procesando...' : `${accion.charAt(0).toUpperCase() + accion.slice(1)} Estudiantes` }}
              </button>
            </div>
          </form>
        </div>

        <!-- Success Modal -->
        <b-modal v-model="mostrarExito" title="¡Proceso Completado!" header-bg-variant="success"
          header-text-variant="light" centered ok-only ok-variant="success" ok-title="Cerrar">
          <div class="text-center py-3">
            <i class="fa fa-check-circle fa-4x text-success mb-3"></i>
            <h5>{{ mensajeExito }}</h5>
            <p class="text-muted">Revise el archivo con los resultados del proceso.</p>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Vue from 'vue';

export default {
  name: 'ActivacionMasiva',
  data() {
    return {
      accion: 'desactivar',
      archivo: null,
      nombreArchivo: '',
      errores: [],
      procesando: false,
      mostrarExito: false,
      mensajeExito: ''
    };
  },
  methods: {
    ...mapActions({
      setWarning: 'setWarning',
    }),
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.archivo = file;
      this.nombreArchivo = file.name;
    },

    async procesarArchivo() {
      if (!this.archivo) {
        this.setWarning('Por favor, carga un archivo válido con números de documento.');
        return;
      }

      this.procesando = true;

      try {
        // Crear FormData para enviar el archivo
        const formData = new FormData();
        formData.append('archivo', this.archivo);
        formData.append('accion', this.accion);

        // Hacer petición con responseType blob para recibir el archivo Excel
        const response = await Vue.http.post('user/procesar-activacion-masiva', formData, {
          responseType: 'blob'
        });

        // Descargar el archivo Excel de resultados
        const blob = new Blob([response.body], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `resultado_${this.accion}_masiva_${new Date().getTime()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Mostrar mensaje de éxito
        this.mensajeExito = `Se ha procesado la ${this.accion === 'activar' ? 'activación' : 'desactivación'} masiva.`;
        this.mostrarExito = true;

        setTimeout(() => {
          this.resetear();
        }, 2000);

      } catch (error) {
        console.error(error);
        const errorMsg = error.body?.message || error.message || 'Ocurrió un error al procesar el archivo.';
        this.setWarning(errorMsg);
      } finally {
        this.procesando = false;
      }
    },

    limpiarArchivo() {
      this.archivo = null;
      this.nombreArchivo = '';
      this.errores = [];
      this.$refs.fileInput.value = '';
    },

    resetear() {
      this.limpiarArchivo();
      this.accion = 'desactivar';
      //this.mostrarExito = false;
    },
  },
};
</script>

<style scoped>
.header-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.5s ease;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0D4E8C 0%, #0a3a6b 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
}

.header-card h2 {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.main-card {
  background: white;
  border-radius: 16px;
  padding: 35px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.6s ease;
}

.section-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

/* Action Selector */
.action-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.action-option {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.action-option:hover {
  border-color: #0D4E8C;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(13, 78, 140, 0.2);
}

.action-option.active {
  border-color: #0D4E8C;
  background: linear-gradient(135deg, rgba(13, 78, 140, 0.1) 0%, rgba(10, 58, 107, 0.1) 100%);
  box-shadow: 0 5px 15px rgba(13, 78, 140, 0.2);
}

.radio-custom {
  width: 24px;
  height: 24px;
  border: 2px solid #cbd5e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.action-option.active .radio-custom {
  border-color: #0D4E8C;
}

.radio-inner {
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #0D4E8C 0%, #0a3a6b 100%);
  border-radius: 50%;
  animation: scaleIn 0.3s ease;
}

.action-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.action-icon {
  font-size: 28px;
}

.action-content strong {
  display: block;
  font-size: 16px;
  color: #2d3748;
  margin-bottom: 2px;
}

/* File Upload */
.file-upload-wrapper {
  position: relative;
}

.file-input {
  display: none;
}

.file-label {
  display: block;
  background: white;
  border: 3px dashed #cbd5e0;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
}

.file-label:hover {
  border-color: #0D4E8C;
  background: rgba(13, 78, 140, 0.05);
}

.file-icon {
  color: #cbd5e0;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.file-label:hover .file-icon {
  color: #0D4E8C;
  transform: scale(1.1);
}

.file-text {
  font-size: 16px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

.file-name {
  color: #0D4E8C;
  font-weight: 600;
}

.file-hint {
  font-size: 13px;
  color: #a0aec0;
}

.btn-clear-file {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f56565;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(245, 101, 101, 0.3);
}

.btn-clear-file:hover {
  background: #e53e3e;
  transform: scale(1.1);
}

/* Demo Download */
.demo-download {
  text-align: center;
}

.demo-link {
  color: #0D4E8C;
  font-weight: 600;
  text-decoration: none;
  font-size: 15px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
}

.demo-link:hover {
  color: #0a3a6b;
  transform: translateX(5px);
}

/* Info Box */
.info-box {
  background: #e6fffa;
  border: 1px solid #81e6d9;
  border-radius: 8px;
  padding: 15px;
}

.info-header {
  font-weight: 600;
  color: #234e52;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.info-content {
  color: #2c7a7b;
  font-size: 14px;
}

.info-content code {
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
  color: #0D4E8C;
  font-weight: 600;
}

.info-content ul {
  padding-left: 20px;
}

.info-content li {
  margin-bottom: 5px;
}

/* Preview */
.preview-stats {
  background: linear-gradient(135deg, #0D4E8C 0%, #0a3a6b 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 20px;
}

.stat-value {
  font-size: 36px;
  font-weight: 700;
}

.stat-label {
  font-size: 16px;
  opacity: 0.9;
}

.preview-list {
  background: white;
  border-radius: 8px;
  padding: 15px;
  max-height: 200px;
  overflow-y: auto;
}

.preview-item {
  padding: 10px;
  border-bottom: 1px solid #e9ecef;
  color: #4a5568;
}

.preview-item:last-child {
  border-bottom: none;
}

.preview-more {
  padding: 10px;
  text-align: center;
  color: #a0aec0;
  font-style: italic;
}

/* Errors */
.error-card {
  background: #fff5f5;
  border: 1px solid #fc8181;
}

.error-list {
  max-height: 200px;
  overflow-y: auto;
}

.error-item {
  color: #c53030;
  padding: 8px 0;
  border-bottom: 1px solid #feb2b2;
}

.error-item:last-child {
  border-bottom: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
}

.action-buttons .btn {
  min-width: 150px;
  font-weight: 600;
  border-radius: 8px;
  padding: 12px 24px;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #0D4E8C 0%, #0a3a6b 100%);
  border: none;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(13, 78, 140, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }

  to {
    transform: scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .action-selector {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .btn {
    width: 100%;
  }

  .header-card h2 {
    font-size: 20px;
  }

  .main-card {
    padding: 20px;
  }
}
</style>